import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { removeAuthToken } from '../../utils/helpers';
import { getNotificationsApi, markNotificationsReadApi } from '../../api/dashboardApi';
import { 
  MagnifyingGlassIcon, 
  Bars3Icon, 
  ChevronDownIcon,
  BellIcon
} from '@heroicons/react/24/outline';

const DashboardHeader = ({ onMenuClick }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await getNotificationsApi();
        const notifs = res.data.data || [];
        setNotifications(notifs);
        if (notifs.length > 0) {
          // Check if there are any notifications not read by the current user
          const unread = notifs.filter(n => !n.readBy || !n.readBy.some(r => r.user === user?._id));
          if (unread.length > 0) {
            setHasUnread(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch notifications', error);
      }
    };
    fetchNotifications();
  }, []);

  const handleNotificationsClick = async () => {
    const isOpening = !isNotificationsOpen;
    setIsNotificationsOpen(isOpening);
    
    if (isOpening && hasUnread && notifications.length > 0) {
      setHasUnread(false);
      try {
        const unreadIds = notifications
          .filter(n => !n.readBy || !n.readBy.some(r => r.user === user?._id))
          .map(n => n._id);
          
        if (unreadIds.length > 0) {
          await markNotificationsReadApi(unreadIds);
        }
      } catch (error) {
        console.error('Failed to mark notifications as read', error);
      }
    }
  };

  const handleLogout = () => {
    removeAuthToken();
    setUser(null);
    navigate('/login');
  };

  return (
    <header className="h-20 bg-white/80 dark:bg-[#0f172a]/80 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800 flex items-center justify-between px-6 lg:px-10 sticky top-0 z-30 shadow-sm transition-colors duration-200">
      
      {/* Left: Search & Mobile Menu */}
      <div className="flex items-center gap-4 flex-1">
        <button 
          onClick={onMenuClick}
          className="lg:hidden p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        
        <div className="hidden md:flex relative w-full max-w-md">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full rounded-2xl border-0 py-2.5 pl-10 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800/50 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#dc343b] sm:text-sm sm:leading-6 transition-all"
            placeholder="Search customers, transactions..."
          />
        </div>
      </div>

      {/* Right: Notifications & Profile Dropdown */}
      <div className="flex items-center gap-4 lg:gap-6">
        
        {/* Notifications Bell */}
        <div className="relative">
          <button 
            onClick={handleNotificationsClick}
            onBlur={() => setTimeout(() => setIsNotificationsOpen(false), 200)}
            className="relative p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors focus:outline-none"
          >
            <BellIcon className="h-6 w-6" />
            {hasUnread && (
              <span className="absolute top-1.5 right-1.5 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-[#0f172a]" />
            )}
          </button>
          
          {/* Notifications Dropdown */}
          {isNotificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden z-50 transform opacity-100 scale-100 transition-all origin-top-right">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Notifications</h3>
                <span className="text-xs font-medium bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{notifications.length} New</span>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">No new notifications</div>
                ) : (
                  notifications.map((notif) => (
                    <div key={notif._id} className="p-4 border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">{notif.title}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-gray-400 mt-2 font-medium">{new Date(notif.createdAt).toLocaleString()}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="hidden sm:flex flex-col items-end">
          <span className="text-[14px] font-bold text-gray-900 dark:text-white">{user?.name || 'Business Owner'}</span>
          <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400">{user?.business?.businessName || 'My Business'}</span>
        </div>

        <div className="relative">
          <button 
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            onBlur={() => setTimeout(() => setIsProfileOpen(false), 200)}
            className="flex items-center gap-2 focus:outline-none group"
          >
            <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-[#dc343b] to-purple-500 p-[2px] shadow-md">
              <div className="h-full w-full rounded-full bg-white dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {user?.name?.charAt(0).toUpperCase() || 'B'}
                </span>
              </div>
            </div>
            <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Profile Dropdown */}
          {isProfileOpen && (
            <div className="absolute right-0 mt-3 w-56 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden z-50 transform opacity-100 scale-100 transition-all origin-top-right">
              <div className="p-2">
                <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 mb-2">
                  <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{user?.email}</p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{user?.business?.businessType} Business</p>
                </div>
                <button 
                  onClick={() => navigate('/settings')}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Settings
                </button>
                <button 
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors mt-1"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </header>
  );
};

export default DashboardHeader;
